import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Switch } from "@/ui/components/ui/switch";
import { SocialLoginButton } from "@/ui/components/ui/social-login-button";
import { EyeIcon, EyeOffIcon } from "@/ui/components/icons";

export interface LoginFormProps {
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Callback when Google sign-in is clicked
   */
  onGoogleSignIn?: () => void;
  /**
   * Callback when Apple sign-in is clicked
   */
  onAppleSignIn?: () => void;
  /**
   * Callback when the main sign-in button is clicked
   */
  onSignIn?: (data: { email: string; password: string; rememberMe: boolean }) => void;
  /**
   * Callback when "Forgot password?" is clicked
   */
  onForgotPassword?: () => void;
  /**
   * Callback when "Sign up now" is clicked
   */
  onSignUp?: () => void;
  /**
   * Loading state for Google sign-in
   */
  googleLoading?: boolean;
  /**
   * Loading state for Apple sign-in
   */
  appleLoading?: boolean;
  /**
   * Loading state for email/password sign-in
   */
  signInLoading?: boolean;
  /**
   * Global error message
   */
  error?: string | null;
  /**
   * Field-specific errors
   */
  fieldErrors?: Record<'email' | 'password', string>;
  /**
   * Session expired message to display
   * Story 4.9: Shown when user's session has expired
   */
  sessionExpiredMessage?: string;
}

/**
 * LoginForm Component
 *
 * A complete login form from Figma design with:
 * - Social login options (Google and Apple) with loading states
 * - Email/phone and password inputs
 * - Password visibility toggle
 * - Remember me switch
 * - Forgot password link
 * - Sign in button with loading state
 * - Sign up offer
 *
 * This component is specific to ANYW3AR and placed in the root components directory.
 *
 * @example
 * ```tsx
 * <LoginForm
 *   onGoogleSignIn={() => console.log('Google sign in')}
 *   onAppleSignIn={() => console.log('Apple sign in')}
 *   onSignIn={(data) => console.log('Sign in', data)}
 *   onForgotPassword={() => console.log('Forgot password')}
 *   onSignUp={() => console.log('Sign up')}
 *   googleLoading={false}
 *   appleLoading={false}
 *   signInLoading={false}
 * />
 * ```
 */
export const LoginForm = React.forwardRef<HTMLDivElement, LoginFormProps>(
  (
    {
      className,
      onGoogleSignIn,
      onAppleSignIn,
      onSignIn,
      onForgotPassword,
      onSignUp,
      googleLoading = false,
      appleLoading = false,
      signInLoading = false,
      error = null,
      fieldErrors,
      sessionExpiredMessage,
    },
    ref
  ) => {
    const { t } = useTranslation('auth');
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [rememberMe, setRememberMe] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!signInLoading) {
        onSignIn?.({ email, password, rememberMe });
      }
    };

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    const isAnyLoading = googleLoading || appleLoading || signInLoading;

    return (
      <div
        ref={ref}
        className={cn(
          "bg-white box-border flex flex-col gap-4 items-center px-4 py-2 rounded-[0.3125rem] w-full",
          className
        )}
      >
        {/* Title */}
        <h2 className="font-montserrat font-semibold text-gray-900 text-xl leading-7 w-full">
          {t('login.title')}
        </h2>

        {/* Session Expired Info Banner (Story 4.9) */}
        {sessionExpiredMessage && (
          <div
            role="status"
            className="w-full bg-blue-100 border border-blue-200 text-blue-800 rounded-[0.3125rem] px-4 py-3 text-sm"
          >
            {sessionExpiredMessage}
          </div>
        )}

        {/* Global Error Banner */}
        {error && (
          <div
            role="alert"
            className="w-full bg-red-100 border border-red-200 text-brand-red rounded-[0.3125rem] px-4 py-3 text-sm"
          >
            {error}
          </div>
        )}

        {/* Social Login Buttons */}
        <SocialLoginButton
          variant="google"
          onClick={onGoogleSignIn}
          className="w-full"
          aria-label={t('login.socialGoogle')}
          disabled={isAnyLoading}
        >
          {googleLoading ? t('loading.signingIn') : t('login.socialGoogle')}
        </SocialLoginButton>

        <SocialLoginButton
          variant="apple"
          onClick={onAppleSignIn}
          className="w-full"
          aria-label={t('login.socialApple')}
          disabled={isAnyLoading}
        >
          {appleLoading ? t('loading.signingIn') : t('login.socialApple')}
        </SocialLoginButton>

        {/* Divider Text */}
        <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 text-center tracking-[0.3px] leading-5">
          {t('login.divider')}
        </p>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
          {/* Email Input */}
          <div className="flex flex-col gap-1 w-full">
            <Input
              type="text"
              variant="anyw3ar"
              placeholder={t('login.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label={t('login.emailPlaceholder')}
              aria-invalid={fieldErrors?.email ? 'true' : 'false'}
              disabled={signInLoading}
              className={cn(fieldErrors?.email && "border-brand-red focus:ring-brand-red")}
            />
            {fieldErrors?.email && (
              <span className="text-brand-red text-sm" role="alert">
                {fieldErrors.email}
              </span>
            )}
          </div>

          {/* Password Input with visibility toggle */}
          <div className="flex flex-col gap-1 w-full">
            <div className="relative w-full">
              <Input
                type={showPassword ? "text" : "password"}
                variant="anyw3ar"
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-label={t('login.passwordPlaceholder')}
                aria-invalid={fieldErrors?.password ? 'true' : 'false'}
                disabled={signInLoading}
                className={cn("pr-12", fieldErrors?.password && "border-brand-red focus:ring-brand-red")}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={showPassword ? "Hide password" : "Show password"}
                disabled={signInLoading}
              >
                {showPassword ? (
                  <EyeOffIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </button>
            </div>
            {fieldErrors?.password && (
              <span className="text-brand-red text-sm" role="alert">
                {fieldErrors.password}
              </span>
            )}
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex flex-col gap-4 w-full">
            {/* Remember Me Switch */}
            <div className="flex gap-2 items-center">
              <Switch
                checked={rememberMe}
                onCheckedChange={setRememberMe}
                aria-label={t('login.rememberMe')}
                disabled={signInLoading}
              />
              <span className="font-montserrat font-normal text-[0.9375rem] text-gray-900 tracking-[0.3px] leading-5">
                {t('login.rememberMe')}
              </span>
            </div>

            {/* Forgot Password Link */}
            <button
              type="button"
              onClick={onForgotPassword}
              className="font-montserrat font-normal text-[0.9375rem] text-brand-red text-right tracking-[0.3px] leading-5 hover:underline focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={signInLoading}
            >
              {t('login.forgotPassword')}
            </button>
          </div>

          {/* Sign In Button */}
          <Button
            type="submit"
            variant="primary-red"
            size="primary-red"
            className="w-full"
            disabled={isAnyLoading}
          >
            {signInLoading ? t('loading.signingIn') : t('login.signIn')}
          </Button>
        </form>

        {/* Divider Line */}
        <div className="bg-gray-100 h-[0.03125rem] w-full" />

        {/* Sign Up Offer */}
        <div className="flex gap-2 items-start font-montserrat font-normal text-xs tracking-[0.3px] leading-5">
          <span className="text-gray-900">{t('login.noAccount')}</span>
          <button
            type="button"
            onClick={onSignUp}
            className="text-brand-red text-right hover:underline focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isAnyLoading}
          >
            {t('login.signUpNow')}
          </button>
        </div>
      </div>
    );
  }
);

LoginForm.displayName = "LoginForm";
